"use client";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { FC, FormEvent, PropsWithChildren, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Edit2Icon, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { handleFormChange } from "@/utils/formChange";
import { editVehicleClass } from "../services/vehicle_service";

interface Props extends PropsWithChildren {
    currency: string;
    title: string;
    description?: string;
    id: number;
    data: any,
    onSaved?: () => void;
}

const ResourceEdit: FC<Props> = ({ currency, title, description, id, data, onSaved }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formProp, setFormProp] = useState({
        name: data.name,
        description: data.description,
        base_price: data.base_price || "",
    });

    const handleEditClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(true);
    };

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await editVehicleClass(id, formProp);
            if (onSaved) {
                onSaved();
            }

            setIsOpen(false);
        } catch (err: any) {
            console.error("Error editing vehicle class:", err);
            toast(err.response?.data?.message || "Internal error occurred");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <DropdownMenuItem onClick={handleEditClick} >
                <Edit2Icon className="mr-2 h-4 w-4" />
                {title}
            </DropdownMenuItem>
            {isOpen && <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="sm:max-w-[600px]" onClick={(e: any) => e.stopPropagation()}>
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                        {description && <DialogDescription>{description}</DialogDescription>}
                    </DialogHeader>
                    <form onSubmit={submit} className="grid gap-1 py-4">
                        <div className="grid gap-1">
                            <Label htmlFor="name" className="text-right">
                                Category name *
                            </Label>
                            <Input id="name" name="name" type="text" value={formProp.name}
                                onChange={(e: FormEvent<HTMLInputElement>) => handleFormChange(e, setFormProp)}
                                placeholder="Name of category" required />
                        </div>
                        <div className="grid gap-1">
                            <Label htmlFor="description" className="text-right">
                                Description
                            </Label>
                            <Input id="description" name="description" type="text" value={formProp.description}
                                onChange={(e: FormEvent<HTMLInputElement>) => handleFormChange(e, setFormProp)} placeholder="Description" />
                        </div>
                        <div className="grid gap-1">
                            <Label htmlFor="base_price" className="text-right">
                                Base price ({currency})
                            </Label>
                            <Input id="base_price" name="base_price" type="number" min={0} value={formProp.base_price}
                                onChange={(e: FormEvent<HTMLInputElement>) => handleFormChange(e, setFormProp)} placeholder="Base price" />
                        </div>
                        <DialogFooter className="w-full mt-3">
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting && (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                Save
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>}
        </>
    );
};

export default ResourceEdit;
